import { t } from "@lingui/macro";
import { experienceSchema } from "@reactive-resume/schema";
import { datesFromText, monthIndex } from "@reactive-resume/utils";
import { z } from "zod";

const monthYearDraft = z
  .object({ year: z.number().nullable(), month: z.number().nullable() })
  .nullable();

// Dates are entered like LinkedIn: month and year for the start, and for the end unless the person
// still works there. Dates read from older text (year only) are kept as they are until edited.
export const experienceFormSchema = experienceSchema
  .extend({
    dates: z
      .object({
        start: monthYearDraft,
        end: monthYearDraft,
        current: z.boolean(),
        source: z.enum(["entered", "parsed", "backfill"]),
      })
      .optional(),
  })
  .superRefine((value, ctx) => {
    const d = value.dates;
    if (!d || d.source !== "entered") return;
    const issue = (message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dates"], message });
    if (!d.start?.year || !d.start.month) return issue(t`Choose the start month and year.`);
    if (d.current) return;
    if (!d.end?.year || !d.end.month) {
      return issue(t`Choose the end month and year, or tick “I currently work here”.`);
    }
    if (
      monthIndex({ year: d.end.year, month: d.end.month }) <
      monthIndex({ year: d.start.year, month: d.start.month })
    ) {
      issue(t`The end date can't be before the start date.`);
    }
  })
  .transform((value) => {
    // An older entry saved without picking dates: its dates are read from its text and kept too.
    if (value.dates || !value.date.trim()) return value;
    const parsed = datesFromText({ date: value.date });
    return parsed ? { ...value, dates: parsed } : value;
  });
